/**
 * Utility functions for user-related operations
 */

/**
 * Get user initials from account name
 * @param accountName - The user's full name from account
 * @returns Initials (2 characters) or 'U' as fallback
 */
export const getUserInitials = (accountName?: string): string => {
  if (accountName) {
    const names = accountName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  }
  return 'U'; // Default fallback for user
};

/**
 * Get the user's selected bubble color from localStorage
 * @returns The user's bubble color or 'default' if none set
 */
export const getUserBubbleColor = (): string => {
  return localStorage.getItem('user-bubble-color') || 'default';
};

/**
 * Compute a readable text color (black or white) for a given bubble background.
 * Supports plain hex colors and simple linear-gradient definitions.
 * For gradients we sample every hex color token and average luminance.
 */
export const computeBubbleTextColor = (background: string): string => {
  if (!background || background === 'default') {
    // "default" bubble uses app background; fall back to theme text variable letting CSS decide.
    return 'var(--color-text)';
  }
  // Extract hex codes from gradient or single value
  const hexMatches = background.match(/#([0-9a-fA-F]{6})/g);
  const hexColors = (hexMatches && hexMatches.length) ? hexMatches : (/^#([0-9a-fA-F]{6})$/.test(background) ? [background] : []);
  if (hexColors.length === 0) {
    // Non-hex value (maybe named color); default to white text assuming dark-ish custom value.
    return '#ffffff';
  }
  // Compute average luminance
  let totalLum = 0;
  hexColors.forEach(h => {
    const r = parseInt(h.slice(1,3),16)/255;
    const g = parseInt(h.slice(3,5),16)/255;
    const b = parseInt(h.slice(5,7),16)/255;
    // sRGB luminance
    const srgb = [r,g,b].map(c => c <= 0.03928 ? c/12.92 : Math.pow((c+0.055)/1.055,2.4));
    const lum = 0.2126*srgb[0] + 0.7152*srgb[1] + 0.0722*srgb[2];
    totalLum += lum;
  });
  const avgLum = totalLum / hexColors.length;
  // Threshold 0.5 roughly splits light vs dark; adjust slightly for mid tones.
  return avgLum > 0.55 ? '#1f2937' : '#ffffff'; // dark gray or white
};